import React, { useState, useEffect, useRef } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { RiDownload2Fill } from "react-icons/ri";
import { FiSearch } from "react-icons/fi";
import { MdNavigateNext, MdNavigateBefore, MdClose, MdAdd, MdRemove, MdRestartAlt } from "react-icons/md";
import './Rates.css'
import Footer from '../Footer/Footer';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const MIN_SCALE = 0.75;
const MAX_SCALE = 2.0;
const SCALE_STEPS = [0.75, 1.0, 1.25, 1.5, 2.0];

function RatesBLL() {
    const location = useLocation();
    const BillundRates =useSelector((state)=>state.BillundRates);
    const [selectedPDF, setSelectedPDF] = useState('');
    const [numPages, setNumPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [mobileZoomLevel, setMobileZoomLevel] = useState(1.0); // Separate state for mobile zoom
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    
    const pdfStageRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const textContentRef = useRef({});
    const highlightTimeoutRef = useRef(null);
    const mobileContainerRef = useRef(null);

    // Detect mobile view
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle search navigation - open specific PDF from search
    useEffect(() => {
        if (BillundRates.length === 0) return;

        // Check if we came from search with a specific PDF title
        if (location.state?.section === 'rates' && location.state?.pdfTitle) {
            const targetPDF = BillundRates.find(pdf => pdf.Title === location.state.pdfTitle);
            if (targetPDF) {
                handlePDFSelect(targetPDF.File);
                return;
            }
        }

        // Default to first PDF if nothing selected
        if (!selectedPDF && BillundRates.length) {
            handlePDFSelect(BillundRates[0].File);
        }
    }, [BillundRates, location.state]);

    const handlePDFSelect = (file) => {
        setLoading(true);
        setSelectedPDF(file);
        setPageNumber(1);
        setSearchTerm('');
        setSearchResults([]);
        setCurrentMatchIndex(0);
        clearAllHighlights();
        textContentRef.current = {};
        setMobileZoomLevel(1.0); // Reset mobile zoom when selecting new PDF
    };

    const onDocumentLoadSuccess = async (pdfInfo) => {
        setNumPages(pdfInfo.numPages);
        setLoading(false);
        
        // Extract text content from all pages
        extractAllTextContent(pdfInfo);
    };

    const extractAllTextContent = async (pdfInfo) => {
        const textContent = {};
        
        try {
            // Use pdfjs directly to get the document
            const loadingTask = pdfjs.getDocument(selectedPDF);
            const pdf = await loadingTask.promise;
            
            for (let i = 1; i <= pdfInfo.numPages; i++) {
                const page = await pdf.getPage(i);
                const content = await page.getTextContent();
                const viewport = page.getViewport({ scale: 1.0 });
                
                textContent[i] = {
                    items: content.items,
                    viewport: viewport
                };
            }
            
            textContentRef.current = textContent;
        } catch (error) {
            console.error('Error extracting text:');
        }
    };

    const onDocumentLoadError = (err) => {
        console.error('PDF load error:');
        setLoading(false);
    };

    // Perform search across all pages
    const performSearch = async (term) => {
        if (!term || Object.keys(textContentRef.current).length === 0) {
            clearAllHighlights();
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        const results = [];
        
        // Search through each page
        Object.keys(textContentRef.current).forEach(pageNum => {
            const pageData = textContentRef.current[pageNum];
            const pageItems = pageData.items;
            
            pageItems.forEach((item, itemIndex) => {
                if (!item.str) return;
                
                // Case-insensitive search
                const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                let match;
                
                while ((match = regex.exec(item.str)) !== null) {
                    results.push({
                        page: parseInt(pageNum),
                        itemIndex,
                        item: item,
                        matchIndex: match.index,
                        matchLength: match[0].length,
                        text: match[0],
                        fullText: item.str,
                        viewport: pageData.viewport
                    });
                }
            });
        });
        
        setSearchResults(results);
        setCurrentMatchIndex(0);
        setIsSearching(false);
        
        // Highlight results
        if (results.length > 0) {
            const currentScale = isMobileView ? mobileZoomLevel : scale;
            highlightSearchResults(results, 0, currentScale);
            scrollToMatch(results[0]);
        }
    };

    // Clear all highlights
    const clearAllHighlights = () => {
        const highlights = document.querySelectorAll('.pdf-search-highlight');
        highlights.forEach(el => el.remove());
        const textLayers = document.querySelectorAll('.pdf-text-layer-search');
        textLayers.forEach(el => el.remove());
    };

    // Highlight search results with accurate positioning
    const highlightSearchResults = (results, activeIndex, currentScale) => {
        // Clear any pending highlight timeout
        if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
        }
        
        clearAllHighlights();
        
        highlightTimeoutRef.current = setTimeout(() => {
            results.forEach((result, index) => {
                // Wait for canvas to be rendered at new scale
                const pageElement = document.querySelector(`[data-page="${result.page}"] canvas`);
                if (!pageElement) return;
                
                const pageWrapper = pageElement.closest('.pdf-page-outer');
                if (!pageWrapper) return;
                
                // Create text layer if it doesn't exist
                let textLayer = pageWrapper.querySelector('.pdf-text-layer-search');
                if (!textLayer) {
                    textLayer = document.createElement('div');
                    textLayer.className = 'pdf-text-layer-search';
                    
                    // For mobile, apply the same transform as the container
                    if (isMobileView) {
                        textLayer.style.position = 'absolute';
                        textLayer.style.top = '0';
                        textLayer.style.left = '0';
                        textLayer.style.width = '100%';
                        textLayer.style.height = '100%';
                        textLayer.style.pointerEvents = 'none';
                    }
                    
                    pageWrapper.appendChild(textLayer);
                }
                
                // Create highlight element
                const highlight = document.createElement('div');
                highlight.className = 'pdf-search-highlight';
                if (index === activeIndex) {
                    highlight.classList.add('pdf-search-highlight--active');
                }
                
                // Calculate position based on PDF coordinates
                const item = result.item;
                const viewport = result.viewport;
                
                if (isMobileView) {
                    // Mobile-specific calculations
                    // The canvas is rendered at (window.innerWidth - 40) and the container is scaled
                    const baseWidth = window.innerWidth - 40;
                    
                    // Calculate base scale (canvas width / original viewport width)
                    const baseScale = baseWidth / viewport.width;
                    
                    // Transform coordinates with base scale (not affected by CSS transform)
                    const tx = item.transform[4] * baseScale;
                    const ty = (baseWidth * viewport.height / viewport.width) - (item.transform[5] * baseScale);
                    
                    // Calculate dimensions with base scale
                    const fontSize = Math.sqrt(Math.pow(item.transform[0], 2) + Math.pow(item.transform[2], 2));
                    const scaledFontSize = fontSize * baseScale;
                    
                    // Use scaled font size for height
                    const height = scaledFontSize * 1.2; // Add some padding
                    
                    // Calculate width based on text length
                    const avgCharWidth = item.width / item.str.length;
                    const width = avgCharWidth * baseScale * item.str.length;
                    
                    // For partial matches, adjust position and width
                    let adjustedX = tx;
                    let adjustedWidth = width;
                    
                    if (result.matchIndex > 0) {
                        // Calculate offset for partial match
                        const beforeMatch = item.str.substring(0, result.matchIndex);
                        const beforeWidth = (beforeMatch.length / item.str.length) * width;
                        adjustedX += beforeWidth;
                    }
                    
                    if (result.matchLength < item.str.length) {
                        // Adjust width for partial match
                        adjustedWidth = (result.matchLength / item.str.length) * width;
                    }
                    
                    highlight.style.position = 'absolute';
                    highlight.style.left = `${adjustedX}px`;
                    highlight.style.top = `${ty - height}px`;
                    highlight.style.width = `${adjustedWidth}px`;
                    highlight.style.height = `${height}px`;
                } else {
                    // Desktop logic (unchanged)
                    // Get actual rendered canvas dimensions (with current scale applied)
                    const rect = pageElement.getBoundingClientRect();
                    const canvasWidth = rect.width;
                    const canvasHeight = rect.height;
                    
                    // Calculate scale factor between original viewport and current canvas size
                    const scaleX = canvasWidth / viewport.width;
                    const scaleY = canvasHeight / viewport.height;
                    
                    // Transform coordinates with current scale
                    const tx = item.transform[4] * scaleX;
                    const ty = canvasHeight - (item.transform[5] * scaleY);
                    
                    // Calculate dimensions with current scale
                    const fontSize = Math.sqrt(Math.pow(item.transform[0], 2) + Math.pow(item.transform[2], 2));
                    const scaledFontSize = fontSize * scaleY;
                    
                    // Use scaled font size for height
                    const height = scaledFontSize * 1.2; // Add some padding
                    
                    // Calculate width based on text length
                    const avgCharWidth = item.width / item.str.length;
                    const width = avgCharWidth * scaleX * item.str.length;
                    
                    // For partial matches, adjust position and width
                    let adjustedX = tx;
                    let adjustedWidth = width;
                    
                    if (result.matchIndex > 0) {
                        // Calculate offset for partial match
                        const beforeMatch = item.str.substring(0, result.matchIndex);
                        const beforeWidth = (beforeMatch.length / item.str.length) * width;
                        adjustedX += beforeWidth;
                    }
                    
                    if (result.matchLength < item.str.length) {
                        // Adjust width for partial match
                        adjustedWidth = (result.matchLength / item.str.length) * width;
                    }
                    
                    highlight.style.position = 'absolute';
                    highlight.style.left = `${adjustedX}px`;
                    highlight.style.top = `${ty - height}px`;
                    highlight.style.width = `${adjustedWidth}px`;
                    highlight.style.height = `${height}px`;
                }
                
                textLayer.appendChild(highlight);
            });
        }, 200); // Give time for canvas to render at new scale
    };

    // Scroll to specific match
    const scrollToMatch = (match) => {
        if (!match) return;
        
        const containerRef = isMobileView ? mobileContainerRef : pdfStageRef;
        if (!containerRef.current) return;
        
        setTimeout(() => {
            const pageElement = document.querySelector(`[data-page="${match.page}"]`);
            if (pageElement) {
                const container = containerRef.current;
                const highlight = document.querySelector('.pdf-search-highlight--active');
                
                if (highlight && isMobileView) {
                    // For mobile, account for the transform scale
                    const pageRect = pageElement.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    const highlightRect = highlight.getBoundingClientRect();
                    
                    // Calculate the actual position accounting for zoom
                    const actualTop = (highlightRect.top - pageRect.top) * mobileZoomLevel + pageElement.offsetTop;
                    
                    // Center the highlight in the viewport
                    const targetScroll = actualTop - (containerRect.height / 2) + (highlightRect.height * mobileZoomLevel / 2);
                    container.scrollTop = Math.max(0, targetScroll);
                    
                    // Also handle horizontal scroll if needed
                    const actualLeft = (highlightRect.left - pageRect.left) * mobileZoomLevel;
                    if (actualLeft < container.scrollLeft || actualLeft > container.scrollLeft + containerRect.width) {
                        container.scrollLeft = Math.max(0, actualLeft - 50);
                    }
                } else if (highlight) {
                    // Desktop scroll logic
                    const pageTop = pageElement.offsetTop;
                    const highlightRect = highlight.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    const relativeTop = highlightRect.top - containerRect.top + container.scrollTop;
                    
                    // Center the highlight in the viewport
                    const targetScroll = relativeTop - (containerRect.height / 2) + (highlightRect.height / 2);
                    container.scrollTop = Math.max(0, targetScroll);
                } else {
                    const pageTop = pageElement.offsetTop;
                    container.scrollTop = pageTop - 50;
                }
                
                // Manually update page number after scrolling
                setTimeout(() => {
                    setPageNumber(match.page);
                }, 50);
            }
        }, 250); // Wait for highlights to be created
    };

    // Handle search input
    const handleSearchInput = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        // Debounce search
        clearTimeout(searchTimeoutRef.current);
        if (value) {
            searchTimeoutRef.current = setTimeout(() => {
                performSearch(value);
            }, 300);
        } else {
            clearAllHighlights();
            setSearchResults([]);
        }
    };

    // Navigate to next match
    const goToNextMatch = () => {
        if (searchResults.length === 0) return;
        const newIndex = (currentMatchIndex + 1) % searchResults.length;
        setCurrentMatchIndex(newIndex);
        const currentScale = isMobileView ? mobileZoomLevel : scale;
        highlightSearchResults(searchResults, newIndex, currentScale);
        scrollToMatch(searchResults[newIndex]);
    };

    // Navigate to previous match
    const goToPreviousMatch = () => {
        if (searchResults.length === 0) return;
        const newIndex = currentMatchIndex === 0 ? searchResults.length - 1 : currentMatchIndex - 1;
        setCurrentMatchIndex(newIndex);
        const currentScale = isMobileView ? mobileZoomLevel : scale;
        highlightSearchResults(searchResults, newIndex, currentScale);
        scrollToMatch(searchResults[newIndex]);
    };

    // Clear search
    const clearSearch = () => {
        setSearchTerm('');
        setSearchResults([]);
        setCurrentMatchIndex(0);
        clearAllHighlights();
    };

    // Re-highlight when scale changes with debounce
    useEffect(() => {
        if (searchResults.length > 0) {
            // Clear any existing timeout
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
            }
            
            // Clear existing highlights immediately
            clearAllHighlights();
            
            // Wait for PDF to re-render at new scale, then re-highlight
            highlightTimeoutRef.current = setTimeout(() => {
                const currentScale = isMobileView ? mobileZoomLevel : scale;
                highlightSearchResults(searchResults, currentMatchIndex, currentScale);
            }, 500); // Increased wait time for mobile transform animation
        }
    }, [scale, mobileZoomLevel, searchResults, currentMatchIndex, isMobileView]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            clearTimeout(searchTimeoutRef.current);
            clearTimeout(highlightTimeoutRef.current);
            clearAllHighlights();
        };
    }, []);

    const snapToStep = (value) => {
        if (SCALE_STEPS.includes(value)) return value;
        let nearest = SCALE_STEPS[0];
        let minDelta = Math.abs(value - nearest);
        for (const step of SCALE_STEPS) {
            const d = Math.abs(value - step);
            if (d < minDelta) { minDelta = d; nearest = step; }
        }
        return nearest;
    };

    const zoomIn = () => {
        preserveScrollPosition(() => {
            setScale(s => {
                const idx = SCALE_STEPS.findIndex(step => step >= s - 0.0001 && step <= s + 0.0001)
                const currentIndex = idx === -1 ? SCALE_STEPS.indexOf(snapToStep(s)) : idx;
                return SCALE_STEPS[Math.min(currentIndex + 1, SCALE_STEPS.length - 1)];
            });
        });
    };

    const zoomOut = () => {
        preserveScrollPosition(() => {
            setScale(s => {
                const idx = SCALE_STEPS.findIndex(step => step >= s - 0.0001 && step <= s + 0.0001)
                const currentIndex = idx === -1 ? SCALE_STEPS.indexOf(snapToStep(s)) : idx;
                return SCALE_STEPS[Math.max(currentIndex - 1, 0)];
            });
        });
    };

    // Mobile zoom functions
    const handleMobileZoomIn = () => {
        setMobileZoomLevel(prev => {
            const newZoom = Math.min(prev + 0.25, MAX_SCALE);
            return newZoom;
        });
    };

    const handleMobileZoomOut = () => {
        setMobileZoomLevel(prev => {
            const newZoom = Math.max(prev - 0.25, MIN_SCALE);
            return newZoom;
        });
    };

    const handleMobileZoomReset = () => {
        setMobileZoomLevel(1.0);
        if (mobileContainerRef.current) {
            mobileContainerRef.current.scrollTop = 0;
            mobileContainerRef.current.scrollLeft = 0;
        }
    };

    const handleZoomSelect = (e) => {
        const val = parseFloat(e.target.value);
        preserveScrollPosition(() => {
            setScale(snapToStep(Math.min(Math.max(val, MIN_SCALE), MAX_SCALE)));
        });
    };

    const preserveScrollPosition = (zoomCallback) => {
        if (!pdfStageRef.current) {
            zoomCallback();
            return;
        }

        const container = pdfStageRef.current;
        const pages = Array.from(container.querySelectorAll('[data-page]'));
        if (!pages.length) {
            zoomCallback();
            return;
        }

        const scrollTop = container.scrollTop;
        let currentPageElement = null;
        let relativePosition = 0;

        for (const el of pages) {
            const pageIdx = parseInt(el.getAttribute('data-page') || '1', 10);
            const top = el.offsetTop;
            const bottom = top + el.clientHeight;
            
            if (bottom > scrollTop + 20) {
                currentPageElement = el;
                const pageTop = el.offsetTop;
                relativePosition = (scrollTop - pageTop) / el.clientHeight;
                break;
            }
        }

        zoomCallback();

        setTimeout(() => {
            if (currentPageElement && pdfStageRef.current) {
                const pageNumber = parseInt(currentPageElement.getAttribute('data-page') || '1', 10);
                const newPageElement = container.querySelector(`[data-page="${pageNumber}"]`);
                
                if (newPageElement) {
                    const newPageTop = newPageElement.offsetTop;
                    const newPageHeight = newPageElement.clientHeight;
                    const newScrollTop = newPageTop + (relativePosition * newPageHeight);
                    container.scrollTop = Math.max(0, newScrollTop);
                }
            }
        }, 100);
    };

    const handleExport = () => {
        if (!selectedPDF) return;
        const link = document.createElement('a');
        link.href = selectedPDF;
        link.download = selectedPDF.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Page tracking on scroll
    useEffect(() => {
        if (!numPages) return;
        
        const container = isMobileView ? mobileContainerRef.current : pdfStageRef.current;
        if (!container) return;
        
        let ticking = false;

        const calcCurrentPage = () => {
            if (!container) return;
            const pages = Array.from(container.querySelectorAll('[data-page]'));
            if (!pages.length) return;
            const scrollTop = container.scrollTop;
            let current = 1;
            for (const el of pages) {
                const pageIdx = parseInt(el.getAttribute('data-page') || '1', 10);
                const top = el.offsetTop;
                const bottom = top + el.clientHeight;
                if (bottom > scrollTop + 20) {
                    current = pageIdx;
                    break;
                }
            }
            setPageNumber(prev => (prev === current ? prev : current));
        };

        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(() => {
                    calcCurrentPage();
                    ticking = false;
                });
            }
        };

        container.addEventListener('scroll', onScroll, { passive: true });
        const initTimer = setTimeout(calcCurrentPage, 60);

        return () => {
            clearTimeout(initTimer);
            container.removeEventListener('scroll', onScroll);
        };
    }, [numPages, scale, mobileZoomLevel, selectedPDF, isMobileView]);

    return (
        <>
        <div className="rates-container">
            <div className="rates-header">
                <h1>RATES SECTION - BILLUND</h1>
            </div>
            <div className="export-row">
                <button className="export-btn" onClick={handleExport} disabled={!selectedPDF}>
                    EXPORT PDF <span className='download-icon'><RiDownload2Fill/></span>
                </button>
            </div>
            
            {/* Mobile Zoom Controls */}
            {isMobileView && (
                <div className="mobile-zoom-controls" style={{
                    position: 'fixed',
                    bottom: '80px',
                    right: '20px',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                }}>
                    <button 
                        className="mobile-zoom-btn"
                        onClick={handleMobileZoomIn}
                        disabled={mobileZoomLevel >= MAX_SCALE}
                        style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            backgroundColor: mobileZoomLevel >= MAX_SCALE ? '#ccc' : '#00238A',
                            color: '#fff',
                            border: 'none',
                            fontSize: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            cursor: mobileZoomLevel >= MAX_SCALE ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        aria-label="Zoom In"
                    >
                        <MdAdd />
                    </button>
                    
                    <button 
                        className="mobile-zoom-btn"
                        onClick={handleMobileZoomOut}
                        disabled={mobileZoomLevel <= MIN_SCALE}
                        style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            backgroundColor: mobileZoomLevel <= MIN_SCALE ? '#ccc' : '#00238A',
                            color: '#fff',
                            border: 'none',
                            fontSize: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            cursor: mobileZoomLevel <= MIN_SCALE ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        aria-label="Zoom Out"
                    >
                        <MdRemove />
                    </button>
                    
                    <button 
                        className="mobile-zoom-btn"
                        onClick={handleMobileZoomReset}
                        style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            backgroundColor: '#F2BA91',
                            color: '#00238A',
                            border: 'none',
                            fontSize: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        aria-label="Reset Zoom"
                    >
                        <MdRestartAlt />
                    </button>
                    
                    <div style={{
                        backgroundColor: '#00238A',
                        color: '#fff',
                        padding: '5px 10px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        marginTop: '5px'
                    }}>
                        {Math.round(mobileZoomLevel * 100)}%
                    </div>
                </div>
            )}
            
            <div className="rates-content rates-grid">
                <aside className="pdf-selector slim">
                    <ul className="pdf-list-menu" role="tablist">
                        {BillundRates.map((pdf) => {
                            const fullPath = pdf.File;
                            const active = selectedPDF === fullPath;
                            return (
                                <li
                                    key={pdf.File}
                                    className={`menu-item ${active ? 'active' : ''}`}
                                    onClick={() => handlePDFSelect(pdf.File)}
                                    role="tab"
                                    aria-selected={active}
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === 'Enter' && handlePDFSelect(pdf.File)}
                                    style={{fontFamily:"Scandinavian New",fontWeight:"900"}}
                                >
                                    <span className={`menu-label ${active? 'activeHead':''}`} style={{fontFamily:"Scandinavian New"}}>
                                        {pdf.Title}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </aside>

                <section className="viewer-pane">
                    <div className="viewer-toolbar">
                        <div style={{fontFamily:"Scandinavian New"}} className="page-indicator">
                            {numPages ? `${pageNumber} / ${numPages}` : ''}
                        </div>
                        
                        {/* Search Controls */}
                        <div className="search-container">
                            <div className="search-input-wrapper">
                                <FiSearch className="searchh-icon" />
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search in PDF..."
                                    value={searchTerm}
                                    onChange={handleSearchInput}
                                />
                                {searchTerm && (
                                    <button type="button" className="clear-search-btn" onClick={clearSearch}>
                                        <MdClose />
                                    </button>
                                )}
                            </div>
                            {searchResults.length > 0 && (
                                <div className="search-results-info">
                                    <span className="match-count">
                                        {currentMatchIndex + 1} of {searchResults.length}
                                    </span>
                                    <button 
                                        type="button" 
                                        className="icon-btn-search" 
                                        onClick={goToPreviousMatch}
                                        disabled={searchResults.length === 0}
                                    >
                                        <MdNavigateBefore />
                                    </button>
                                    <button 
                                        type="button" 
                                        className="icon-btn-search" 
                                        onClick={goToNextMatch}
                                        disabled={searchResults.length === 0}
                                    >
                                        <MdNavigateNext />
                                    </button>
                                </div>
                            )}
                            {isSearching && (
                                <span className="searching-indicator">Searching...</span>
                            )}
                        </div>

                        {!isMobileView && (
                            <div className="zoom-controls">
                                <button className="icon-btn" onClick={zoomOut} disabled={scale <= MIN_SCALE} aria-label="Zoom out">
                                    -
                                </button>
                                <select className="zoom-select" value={scale} onChange={handleZoomSelect} aria-label="Zoom level">
                                    {SCALE_STEPS.map(z => <option key={z} value={z}>{Math.round(z * 100)}%</option>)}
                                </select>
                                <button className="icon-btn" onClick={zoomIn} disabled={scale >= MAX_SCALE} aria-label="Zoom in">
                                    +
                                </button>
                            </div>
                        )}
                    </div>
                    
                    {/* Mobile View with Custom Zoom */}
                    {isMobileView ? (
                        <div 
                            className="mobile-pdf-container"
                            ref={mobileContainerRef}
                            style={{
                                width: '100%',
                                height: 'calc(100vh - 250px)',
                                overflow: 'auto',
                                position: 'relative',
                                WebkitOverflowScrolling: 'touch'
                            }}
                        >
                            {loading && <div className="loading-overlay">Loading PDF…</div>}
                            {selectedPDF && (
                                <div style={{
                                    transform: `scale(${mobileZoomLevel})`,
                                    transformOrigin: 'top left',
                                    width: `${100 / mobileZoomLevel}%`,
                                    minWidth: 'fit-content'
                                }}>
                                    <Document
                                        file={selectedPDF}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        onLoadError={onDocumentLoadError}
                                        loading=""
                                        className="pdf-document-mobile"
                                    >
                                        {numPages > 0 && Array.from(new Array(numPages), (_, idx) => (
                                            <div
                                                key={`mobile_page_${idx + 1}`}
                                                data-page={idx + 1}
                                                className="pdf-page-outer-mobile pdf-page-outer"
                                                style={{ marginBottom: '20px', position: 'relative' }}
                                            >
                                                <Page
                                                    key={`mobile_page_${idx + 1}_${mobileZoomLevel}`}
                                                    pageNumber={idx + 1}
                                                    renderAnnotationLayer={false}
                                                    renderTextLayer={false}
                                                    className="pdf-page-mobile"
                                                    width={window.innerWidth - 40}
                                                />
                                            </div>
                                        ))}
                                    </Document>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Desktop View */
                        <div className="pdf-stage" ref={pdfStageRef} style={{ overflow: 'auto', maxWidth: '100%' }}>
                            {loading && <div className="loading-overlay">Loading PDF…</div>}
                            {selectedPDF && (
                                <div style={{ 
                                    display: 'inline-block', 
                                    minWidth: 'fit-content'
                                }}>
                                    <Document
                                        file={selectedPDF}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        onLoadError={onDocumentLoadError}
                                        loading=""
                                        className="pdf-document pdf-document-multi"
                                    >
                                        {numPages > 0 && Array.from(new Array(numPages), (_, idx) => (
                                            <div
                                                key={`page_wrap_${idx + 1}`}
                                                data-page={idx + 1}
                                                className="pdf-page-outer"
                                            >
                                                <Page
                                                    key={`page_${idx + 1}_${scale}`}
                                                    pageNumber={idx + 1}
                                                    scale={scale}
                                                    renderAnnotationLayer={false}
                                                    renderTextLayer={false}
                                                    className="pdf-page-wrapper"
                                                />
                                            </div>
                                        ))}
                                    </Document>
                                </div>
                            )}
                        </div>
                    )}
                </section>
            </div>
        </div>
        <Footer/>
        </>     
    );
}

export default RatesBLL;